import { getGroupSearchConfigs, getServiceConfig, sortFeatures } from "./SearchUtils";
import {
    replaceDiacritics,
    Config as GeoJSConfig,
    Query,
    Layer as GeoJSLayer,
    IFeature as GeoJSFeature
} from "@geo/js";
import { IGroup } from "../../Contexts/SearchContext";
import { executeQuery } from "../rest";
import { Feature } from "ol";

export interface IQueryData {
    url: string;
    layer: any;
    searchParam: string;
}

export const buildQueries = (searchText: string, config, selectedGroup: IGroup, onError): IQueryData[] => {
    const searchConfig = new GeoJSConfig(config.search ? config.search.groups : config);
    const searchConfigs = getGroupSearchConfigs(config, selectedGroup);
    const queryConfigs = searchConfig.getValidQueryConfigs(searchText, searchConfigs);

    (!queryConfigs || queryConfigs.length === 0) && onError();

    return queryConfigs.map((service) => {
        const serviceLayer = getServiceConfig(service.serviceId, config.layers);
        return serviceLayer && prepareQuery(service, searchText, serviceLayer);
    });
};

const prepareQuery = (service, searchParam: string, serviceLayer): IQueryData =>
    service.layerConfigs.map((layer) => {
        const url =
            serviceLayer.type === "FeatureLayer" || serviceLayer.type === "ClusterLayer"
                ? serviceLayer.url + "/" + serviceLayer.layerId
                : serviceLayer.url + "/" + layer.id;

        layer.url = url;
        layer.serviceId = service.serviceId;

        return {
            url,
            layer,
            searchParam
        };
    });

export const executeOracleTextSearchQuery = async (
    layerUrl: string,
    layerConfig,
    searchParam: string
): Promise<Feature[]> => {
    const queryResults: Feature[] = [].concat(
        ...(await Promise.all([
            getQueryResults(layerUrl, buildQuery(layerConfig, searchParam)),
            getQueryResults(layerUrl, buildQuery(layerConfig, searchParam, true)),
            getQueryResults(layerUrl, buildQuery(layerConfig, searchParam, false, true)),
            getQueryResults(layerUrl, buildQuery(layerConfig, searchParam, true, true))
        ]))
    );
    const merged: Feature[] = [];
    queryResults.map((r) => !merged.some((m) => m.get("OBJECTID") === r.get("OBJECTID")) && merged.push(r));

    return merged;
};

export const executeDefaultQuery = async (layerUrl: string, layerConfig, searchParam: string) => {
    const query = buildQuery(layerConfig, searchParam);
    try {
        return await getQueryResults(layerUrl, query);
    } catch (e) {
        console.log(e);
        return;
    }
};

const getQueryResults = async (layerUrl: string, query) => {
    const results = await executeQuery(layerUrl, query);
    return sortFeatures(results);
};

const buildQuery = (layerConfig, searchParam: string, isNonLocale?: boolean, isPartial?: boolean) => {
    const queryJS = new Query();
    const searchText = isNonLocale ? replaceDiacritics(searchParam) : searchParam;
    const params = queryJS.prepareParam(searchText, layerConfig.type ? layerConfig.type : false, isPartial);
    let layerQuery = isNonLocale ? layerConfig.queryNonLocale : layerConfig.query;
    layerQuery = (layerQuery ? layerQuery : "").replace(new RegExp("\\{0\\}", "g"), params.toUpperCase());
    const query = {
        f: "json",
        returnGeometry: true,
        outFields: "*",
        where: layerQuery
    };
    return query;
};
